import { X, User, Lock, FileText, ChevronRight, Link2, Instagram, Linkedin, Youtube, Facebook, Camera, ImageIcon, Check, LogOut, Trash2, Users, Bell, CheckCircle2, Shield, Calendar, RefreshCw, BarChart3, Loader2, ExternalLink, HelpCircle, Activity, Code, BookOpen, Film, Armchair, MessageSquare, Image, Building2, FolderOpen, Lightbulb } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { TikTokIcon } from '../components/ui/TikTokIcon';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { isStaff } from '@/services/data/roles';
import { useServiceAccess } from '@/hooks/useServiceAccess';
import { toast } from 'sonner';
import { pickPhotoFromLibrary, takePhoto, type PhotoResult } from '../services/photoLibrary';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { useDebouncedCallback } from '../hooks/useDebouncedCallback';
import { useIsMobile } from '@/hooks/useMobile';
import { getAllPlatforms, updateConnectionStatus, initializeUserPlatforms, syncMetricoolConnectionsToDb, getConnectionLink } from '@/services/data/socialConnections';
import { useSocialNetworkConnect } from '@/hooks/useSocialNetworkConnect';
import { openMetricoolUrl } from '@/utils/metricoolNavigation';
import { ImageCropper } from '../components/ImageCropper';
import { getUserAssignments } from '@/services/data/staffAssignment';
import type { Director } from '@/services/data/directors';
import type { PersonalBrandManager } from '@/services/data/personalBrandManagers';
import { KnowledgeBaseForm } from '../components/KnowledgeBaseForm';
import { KnowledgeBaseWizard } from '../components/KnowledgeBaseWizard';
import { usePreloadAvatars } from '@/hooks/usePreloadAvatars';
import { getUserProfile, updateUserProfile, updateAvatarUrl, updateHasSignedUp, getUserById } from '@/services/data/users';
import { useAuthStore } from '@/stores/authStore';
import { useImpersonation } from '@/hooks/useEffectiveUserId';
import { useUserStore } from '@/stores/userStore';
import { uploadAvatar, deleteAvatar } from '@/services/data/avatarStorage';
import { verifyCurrentPassword, updatePassword } from '@/services/data/auth';
import { NotificationOnboardingScreen } from './NotificationOnboardingScreen';
import { useNotificationStore } from '@/stores/notificationStore';
import { useDisconnectedNetworksStore } from '@/stores/disconnectedNetworksStore';
import { useKnowledgeBaseStore } from '@/stores/knowledgeBaseStore';
import { useStaffLeaveStore } from '@/stores/staffLeaveStore';
import { useChatNavigationStore } from '@/stores/chatNavigationStore';
import { UserAssetsSection } from '@/components/UserAssetsSection';
import { DesktopProfileLayout } from '@/components/profile/DesktopProfileLayout';
import { ProfileCard, ProfileCardIcon, ProfileCardStatus } from '@/components/profile/ProfileCard';
// DesktopProfileSkeleton removed - using progressive loading in DesktopProfileLayout
import { getUserPlanningPreferences } from '@/services/data/planningPreferences';
import { getShootingPreferences } from '@/services/data/shootingPreferences';
import { getOrCreateConversationForUser } from '@/services/data/chat';


// Helper functions for consistent avatar styling across the app
function getInitials(firstName?: string | null, lastName?: string | null): string {
  const first = firstName?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.charAt(0)?.toUpperCase() || '';
  return first + last || '?';
}

function getGradientFromName(firstName?: string | null, lastName?: string | null): string {
  const name = `${firstName || ''}${lastName || ''}`;
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    const codePoint = name.codePointAt(i) || 0;
    hash = codePoint + ((hash << 5) - hash);
  }
  
  const gradients = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
  ];
  
  return gradients[Math.abs(hash) % gradients.length];
}

// Map view names to service names in role_permissions (same as DesktopSidebar)
const VIEW_TO_SERVICE: Record<string, string> = {
  'admin-create-user': 'admin_create_user',
  'admin-delete-user': 'admin_delete_user',
  'admin-users': 'admin_feature_access',
  'admin-notifications': 'admin_notifications',
  'admin-feature-access': 'admin_feature_access',
  'admin-fake-content': 'admin_fake_content',
  'admin-user-view': 'admin_user_view',
  'admin-network-prompts': 'admin_feature_access',
  'admin-stats': 'admin_stats',
  'admin-api-test': 'admin_stats',
  'admin-sync-dashboard': 'admin_stats',
  'admin-documentation': 'admin_stats',
  'admin-studios': 'admin_feature_access',
  'assets': 'rushes_manager',
};

// Admin menu items - synced with DesktopSidebar adminMenuItems
const adminMenuItems: Array<{ label: string; view: string; icon: typeof Users }> = [
  { label: "Gestion utilisateurs", view: "admin-users", icon: Users },
  { label: "Notifications", view: "admin-notifications", icon: Bell },
  { label: "Fake content", view: "admin-fake-content", icon: Film },
  { label: "Product Board", view: "product-board", icon: Lightbulb },
  
  { label: "Statistiques", view: "admin-stats", icon: BarChart3 },
  { label: "Sync Queue", view: "admin-sync-dashboard", icon: Activity },
  { label: "API Test", view: "admin-api-test", icon: Code },
  { label: "Documentation", view: "admin-documentation", icon: BookOpen },
  { label: "Studios", view: "admin-studios", icon: Building2 },
  { label: "Assets", view: "assets", icon: FolderOpen },
];
interface UserInfoScreenProps {
  userEmail: string;
  onLogout: () => void;
  onDeleteAccount: () => void;
}

// Mock data - dans une vraie app, ces données viendraient du backend
const mockUserData = {
  firstName: 'Jean',
  lastName: 'Dupont',
  email: '',
  invoices: [{
    id: 1,
    date: '2024-12-15',
    plan: 'Émergence',
    phase: 2,
    totalPhases: 2,
    amount: 3000,
    status: 'À venir'
  }, {
    id: 2,
    date: '2024-12-08',
    plan: 'Présence',
    phase: 3,
    totalPhases: 6,
    amount: 1500,
    status: 'À venir'
  }, {
    id: 3,
    date: '2024-11-20',
    plan: 'Émergence',
    phase: 1,
    totalPhases: 2,
    amount: 3000,
    status: 'Payée'
  }, {
    id: 4,
    date: '2024-10-30',
    plan: 'Accélération',
    phase: 1,
    totalPhases: 1,
    amount: 1000,
    status: 'Payée'
  }, {
    id: 5,
    date: '2024-09-15',
    plan: 'Présence',
    phase: 2,
    totalPhases: 6,
    amount: 1500,
    status: 'Payée'
  }, {
    id: 6,
    date: '2024-08-10',
    plan: 'Présence',
    phase: 1,
    totalPhases: 6,
    amount: 1500,
    status: 'Payée'
  }]
};
type SocialNetwork = {
  id: string;
  name: string;
  platform: string; // clé originale de la DB (linkedin, instagram, etc.)
  icon: any;
  connected: boolean;
  username?: string;
};

// Helper functions pour mapper les plateformes
const getPlatformName = (platform: string): string => {
  const names: Record<string, string> = {
    linkedin: 'LinkedIn',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    youtube: 'YouTube',
    facebook: 'Facebook'
  };
  return names[platform] || platform;
};
const getPlatformIcon = (platform: string) => {
  const icons: Record<string, any> = {
    linkedin: Linkedin,
    instagram: Instagram,
    tiktok: TikTokIcon,
    youtube: Youtube,
    facebook: Facebook
  };
  return icons[platform] || Link2;
};
export function UserInfoScreen({
  userEmail,
  onLogout,
  onDeleteAccount
}: UserInfoScreenProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [currentPasswordError, setCurrentPasswordError] = useState('');
  const [profileImage, setProfileImage] = useState<string>('');
  const [isPhotoDialogOpen, setIsPhotoDialogOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // Progressive loading states per section
  const [loadingState, setLoadingState] = useState({
    profile: true,
    team: true,
    knowledgeBase: true,
    socialNetworks: true,
    planning: true,
    shooting: true,
    design: true,
    description: true,
    resources: true,
  });
  const [isChangingPasswordLoading, setIsChangingPasswordLoading] = useState(false);
  const [tempImageForCrop, setTempImageForCrop] = useState<string>('');
  const [isCropperOpen, setIsCropperOpen] = useState(false);

  // États pour les champs éditables
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [email] = useState(userEmail);
  // Extended type with user data mapped from users table
  type DirectorWithUserData = Director & {
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    avatar_url?: string | null;
  };
  type PBMWithUserData = PersonalBrandManager & {
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    avatar_url?: string | null;
  };

  // États pour le staff attribué
  const [assignedDirector, setAssignedDirector] = useState<DirectorWithUserData | null>(null);
  const [assignedPBM, setAssignedPBM] = useState<PBMWithUserData | null>(null);
  const [assignedOps, setAssignedOps] = useState<{
    id: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
    job_title: string | null;
  } | null>(null);
  const [userId, setUserId] = useState<string>('');

  // États pour la knowledge base
  const [showKnowledgeBase, setShowKnowledgeBase] = useState(false);
  const [showKnowledgeWizard, setShowKnowledgeWizard] = useState(false);
  const [hasKnowledgeBase, setHasKnowledgeBase] = useState(false);
  const [knowledgeBaseProgress, setKnowledgeBaseProgress] = useState({
    completed: 0,
    total: 12
  });

  // États pour les notifications
  const [showNotificationOnboarding, setShowNotificationOnboarding] = useState(false);
  const {
    notificationOnboardingComplete
  } = useNotificationStore();

  // État pour le rôle admin
  const [isAdmin, setIsAdmin] = useState(false);
  const [appVersion, setAppVersion] = useState<string | null>(null);
  const [globalFrequency, setGlobalFrequency] = useState<number | null>(null);
  const [isAutoPlanning, setIsAutoPlanning] = useState<boolean | null>(null);
  const [shootingPrefs, setShootingPrefs] = useState<{ installationType: string | null; decorColor: string | null } | null>(null);
  const [thumbnailStyle, setThumbnailStyle] = useState<string | null>(null);
  const [hasDesignPrefs, setHasDesignPrefs] = useState(false);
  const [toneOfVoice, setToneOfVoice] = useState<string | null>(null);
  const [hasEmojis, setHasEmojis] = useState<boolean | null>(null);
  const [hasRecurringCta, setHasRecurringCta] = useState<boolean | null>(null);
  const [hasDescriptionPrefs, setHasDescriptionPrefs] = useState(false);
  const [disabledNetworks, setDisabledNetworks] = useState<string[]>([]);

  // Staff leave store
  const { isOnLeave, isLoading: isLeaveLoading, fetchLeaveStatus, toggleLeave } = useStaffLeaveStore();

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      CapacitorApp.getInfo().then((info) => {
        const v = info.version ?? '';
        const b = info.build ?? '';
        setAppVersion(b ? `${v} (${b})` : v || null);
      });
    }
  }, []);

  // Hook pour les permissions de service (même logique que DesktopSidebar)
  const { hasAccess } = useServiceAccess();
  
  useSocialNetworkConnect();
  const [linkLoadingPlatform, setLinkLoadingPlatform] = useState<string | null>(null);
  // Track if connection link fetch failed (500 error) to show fallback UI
  const [connectionLinkFailed, setConnectionLinkFailed] = useState(false);

  // Ouvrir le wizard si demandé via navigation state
  useEffect(() => {
    if (location.state?.openKnowledgeWizard && userId) {
      setShowKnowledgeWizard(true);
      // Clear the state to avoid reopening on next visit
      window.history.replaceState({}, document.title);
    }
  }, [location.state, userId]);

  // Précharger les avatars
  usePreloadAvatars({
    userAvatar: profileImage,
    directorAvatar: assignedDirector?.avatar_url,
    pbmAvatar: assignedPBM?.avatar_url
  });

  // Get session ready state from auth store
  const {
    isSessionReady
  } = useAuthStore();

  // Get impersonation state to use effective userId
  const { effectiveUserId } = useImpersonation();

  // Fonction réutilisable pour rafraîchir le progress de la knowledge base
  const refreshKnowledgeBaseProgress = async (targetUserId?: string) => {
    const authState = useAuthStore.getState();
    if (!authState.isSessionReady) return;
    const userIdToUse = targetUserId || effectiveUserId || authState.userId;
    if (!userIdToUse) return;
    try {
      const {
        getKnowledgeBaseResponse
      } = await import('@/services/data/knowledgeBase');
      const kbResult = await getKnowledgeBaseResponse(userIdToUse);
      if (kbResult.success && kbResult.data) {
        const kbData = kbResult.data as typeof kbResult.data & {
          media_links?: string[];
        };
        const textFields = ['objectif', 'echec_si', 'domaines_parole', 'unique_different', 'cible_se_trompe', 'cible_inquiete', 'cible_parle_de', 'cible_reve', 'opinions_tranchees', 'ne_laisserai_jamais'];
        const textCount = textFields.filter(field => {
          const value = kbData[field as keyof typeof kbData];
          return value && typeof value === 'string' && (value as string).trim() !== '';
        }).length;
        const cibleData = kbData.cible as string[] | null;
        const cibleFilled = cibleData && Array.isArray(cibleData) && cibleData.length > 0 && cibleData.some(v => v.trim() !== '');
        const mediaLinksData = kbData.media_links as string[] | null;
        const mediaLinksFilled = mediaLinksData && Array.isArray(mediaLinksData) && mediaLinksData.length > 0 && mediaLinksData.some(v => v.trim() !== '');
        const totalFields = textFields.length + 2;
        const completedCount = textCount + (cibleFilled ? 1 : 0) + (mediaLinksFilled ? 1 : 0);
        const allFieldsFilled = completedCount === totalFields;
        setKnowledgeBaseProgress({
          completed: completedCount,
          total: totalFields
        });
        setHasKnowledgeBase(allFieldsFilled);
      } else {
        // No KB data means nothing is filled
        setKnowledgeBaseProgress({
          completed: 0,
          total: 12
        });
        setHasKnowledgeBase(false);
      }
    } catch (error) {
      console.error('[refreshKnowledgeBaseProgress] Error:', error);
    }
  };

  // Charger les données du profil depuis Supabase
  useEffect(() => {
    const loadProfile = async () => {
      // Wait for session to be ready before making authenticated requests
      const authState = useAuthStore.getState();
      if (!authState.isSessionReady) {
        return;
      }
      try {
        // Use effectiveUserId (impersonated or real user)
        const currentUserId = effectiveUserId || authState.userId;
        
        if (!currentUserId) {
          setIsLoading(false);
          setLoadingState(prev => ({
            ...prev,
            profile: false,
            team: false,
            knowledgeBase: false,
            socialNetworks: false,
            planning: false,
            shooting: false,
            design: false,
            description: false,
            resources: false,
          }));
          return;
        }
        
        setUserId(currentUserId);

        // Load profile data first (quick) then mark as loaded
        const profileResult = await getUserProfile(currentUserId);
        if (profileResult.success && profileResult.data) {
          const profile = profileResult.data;
          setFirstName(profile.first_name || '');
          setLastName(profile.last_name || '');
          setPhone(profile.phone || '');
          setJobTitle(profile.job_title || '');
          if (profile.avatar_url) {
            setProfileImage(profile.avatar_url);
          }
        }
        // Mark profile as loaded immediately
        setLoadingState(prev => ({ ...prev, profile: false }));
        setIsLoading(false); // Main loading done, show layout

        // Load remaining data in parallel with progressive updates
        const [
          socialSyncDone,
          assignmentsResult,
          staffResult,
          planningResult,
          shootingResult,
          _kbResult
        ] = await Promise.all([
          // Social networks (includes sync)
          (async () => {
            await syncMetricoolConnectionsToDb(currentUserId);
            await initializeUserPlatforms(currentUserId);
            const socialResult = await getAllPlatforms(currentUserId);
            if (socialResult.success && socialResult.data) {
              const networksFromDb = socialResult.data.map(connection => ({
                id: connection.id,
                name: getPlatformName(connection.platform),
                platform: connection.platform,
                icon: getPlatformIcon(connection.platform),
                connected: connection.connected,
                username: connection.username || undefined
              }));
              setSocialNetworks(networksFromDb);
            }
            setLoadingState(prev => ({ ...prev, socialNetworks: false }));
            return true;
          })(),
          
          // Team assignments
          (async () => {
            const result = await getUserAssignments(currentUserId);
            if (result.success && result.data) {
              setAssignedDirector(result.data.director || null);
              setAssignedPBM(result.data.personal_brand_manager || null);
              setAssignedOps(result.data.ops || null);
            }
            setLoadingState(prev => ({ ...prev, team: false }));
            return result;
          })(),
          
          // Staff check
          isStaff(authState.userId!),
          
          // Planning preferences
          (async () => {
            const result = await getUserPlanningPreferences(currentUserId);
            if (result.success && result.data) {
              if (result.data.global_frequency) {
                setGlobalFrequency(result.data.global_frequency);
              }
              setIsAutoPlanning(result.data.is_auto_planning ?? true);
              setDisabledNetworks(result.data.disabled_networks || []);
              // Description preferences come from planning
              setToneOfVoice(result.data.tone_of_voice || null);
              setHasEmojis(result.data.has_emojis ?? null);
              setHasRecurringCta(result.data.has_recurring_cta ?? null);
              setHasDescriptionPrefs(
                !!result.data.tone_of_voice && 
                typeof result.data.has_emojis === 'boolean' && 
                typeof result.data.has_recurring_cta === 'boolean'
              );
            }
            setLoadingState(prev => ({ ...prev, planning: false, description: false }));
            return result;
          })(),
          
          // Shooting preferences (includes design)
          (async () => {
            const result = await getShootingPreferences(currentUserId);
            if (result.success && result.data) {
              setShootingPrefs({
                installationType: result.data.installation_type,
                decorColor: result.data.decor_color
              });
              setThumbnailStyle(result.data.thumbnail_style || null);
              setHasDesignPrefs(!!result.data.thumbnail_style);
            }
            setLoadingState(prev => ({ ...prev, shooting: false, design: false }));
            return result;
          })(),
          
          // Knowledge base
          (async () => {
            await refreshKnowledgeBaseProgress(currentUserId);
            setLoadingState(prev => ({ ...prev, knowledgeBase: false }));
            return true;
          })(),
          
          // Resources - mark as ready (component handles its own data loading)
          (async () => {
            setLoadingState(prev => ({ ...prev, resources: false }));
            return true;
          })()
        ]);

        const isStaffMember = staffResult.success && staffResult.data === true;
        setIsAdmin(isStaffMember);
        // Fetch leave status for staff members using the real authenticated userId
        if (isStaffMember && authState.userId) {
          fetchLeaveStatus(authState.userId);
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
        // On error, stop all loading indicators
        setLoadingState({
          profile: false,
          team: false,
          knowledgeBase: false,
          socialNetworks: false,
          planning: false,
          shooting: false,
          design: false,
          description: false,
          resources: false,
        });
      } finally {
        setIsLoading(false);
      }
    };
    loadProfile();
  }, [isSessionReady, effectiveUserId]); // Re-run when session becomes ready OR when impersonated user changes

  // Subscribe to social connections changes from other components (e.g., calendar)
  const socialConnectionsVersion = useDisconnectedNetworksStore(state => state.socialConnectionsVersion);
  useEffect(() => {
    const refreshSocialNetworks = async () => {
      const authState = useAuthStore.getState();
      if (!authState.isSessionReady) return;
      const userIdToUse = effectiveUserId || authState.userId;
      if (!userIdToUse) return;
      const socialResult = await getAllPlatforms(userIdToUse);
      if (socialResult.success && socialResult.data) {
        const networksFromDb = socialResult.data.map(connection => ({
          id: connection.id,
          name: getPlatformName(connection.platform),
          platform: connection.platform,
          icon: getPlatformIcon(connection.platform),
          connected: connection.connected,
          username: connection.username || undefined
        }));
        setSocialNetworks(networksFromDb);
      }
    };

    // Only refresh if version > 0 (skip initial render)
    if (socialConnectionsVersion > 0) {
      refreshSocialNetworks();
    }
  }, [socialConnectionsVersion, effectiveUserId]);
  useEffect(() => {
  }, [showKnowledgeBase, showKnowledgeWizard]);

  // Fonction pour uploader une image vers Supabase Storage
  const uploadAvatarToStorage = async (imageBlob: Blob): Promise<string | null> => {
    try {
      const authState = useAuthStore.getState();
      if (!authState.userId) return null;
      return await uploadAvatar(authState.userId, imageBlob);
    } catch (error) {
      console.error('Error uploading avatar:', error);
      return null;
    }
  };
  const [hasPersonalInfoChanges, setHasPersonalInfoChanges] = useState(false);
  const [socialNetworks, setSocialNetworks] = useState<SocialNetwork[]>([]);
  const userData = {
    firstName,
    lastName,
    email,
    invoices: mockUserData.invoices
  };
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const handleSavePersonalInfo = async () => {
    if (isSavingProfile) return;
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('Le prénom et le nom sont requis');
      return;
    }
    setIsSavingProfile(true);
    try {
      const authState = useAuthStore.getState();
      if (!authState.userId) {
        toast.error('Impossible de récupérer l\'utilisateur');
        return;
      }
      const result = await updateUserProfile(authState.userId, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim() || null,
        job_title: jobTitle.trim() || null
      });
      if (!result.success) throw result.error;
      toast.success('Informations personnelles mises à jour avec succès');
      setHasPersonalInfoChanges(false);
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSavingProfile(false);
    }
  };
  const changePasswordInternal = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Tous les champs sont requis');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    setIsChangingPasswordLoading(true);
    try {
      // Vérifier que l'utilisateur est connecté via le store
      const authState = useAuthStore.getState();
      if (!authState.userId || !authState.userEmail) {
        toast.error('Vous devez être connecté');
        return;
      }

      // Vérifier d'abord le mot de passe actuel en essayant de se reconnecter
      const verifyResult = await verifyCurrentPassword(authState.userEmail, currentPassword);
      if (!verifyResult.success) {
        setCurrentPasswordError('Le mot de passe actuel est incorrect');
        return;
      }

      // Changer le mot de passe
      const updateResult = await updatePassword(newPassword);
      if (!updateResult.success) {
        console.error('Error updating password:', updateResult.error);
        toast.error('Erreur lors du changement de mot de passe');
        return;
      }

      // Mettre à jour le flag has_signed_up dans la base
      await updateHasSignedUp(authState.userId, true);
      toast.success('Mot de passe modifié avec succès');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setIsChangingPassword(false);
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Erreur lors du changement de mot de passe');
    } finally {
      setIsChangingPasswordLoading(false);
    }
  };
  const [handleChangePassword] = useDebouncedCallback(changePasswordInternal, 500);
  const downloadInvoiceInternal = (plan: string, phase: number, totalPhases: number) => {
    // Mock function - no actual download implemented
  };
  const [handleDownloadInvoice] = useDebouncedCallback(downloadInvoiceInternal, 500);
  const toggleSocialNetworkInternal = async (id: string) => {
    const network = socialNetworks.find(n => n.id === id);
    if (!network) return;
    const newConnected = !network.connected;

    // Générer des données OAuth factices lors de la connexion
    const generateFakeOAuthData = () => {
      const randomId = crypto.randomUUID().replace(/-/g, '').slice(0, 16);
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 30); // Expire dans 30 jours

      return {
        connected: true,
        is_active: true,
        username: `@prsnl_${network.platform}`,
        platform_user_id: `fake_usr_${randomId}`,
        access_token: `fake_at_${crypto.randomUUID().replace(/-/g, '')}`,
        refresh_token: `fake_rt_${crypto.randomUUID().replace(/-/g, '')}`,
        expires_at: expiresAt.toISOString()
      };
    };

    // Données à sauvegarder selon connexion/déconnexion
    const updateData = newConnected ? generateFakeOAuthData() : {
      connected: false,
      is_active: false,
      username: null,
      platform_user_id: null,
      access_token: null,
      refresh_token: null,
      expires_at: null
    };

    // Mettre à jour le state local
    setSocialNetworks(prev => prev.map(n => {
      if (n.id === id) {
        return {
          ...n,
          connected: newConnected,
          username: newConnected ? updateData.username : undefined
        };
      }
      return n;
    }));

    // Mettre à jour la base de données
    await updateConnectionStatus(id, updateData);

    // Rafraîchir le store des réseaux déconnectés pour mettre à jour le calendrier
    const authState = useAuthStore.getState();
    if (authState.userId) {
      useDisconnectedNetworksStore.getState().fetchDisconnectedNetworks(authState.userId);
    }
  };
  const [handleToggleSocialNetwork] = useDebouncedCallback(toggleSocialNetworkInternal, 500);
  
  const handleConnectSocialNetwork = async (platform: string) => {
    const userIdToUse = effectiveUserId || useAuthStore.getState().userId;
    if (!userIdToUse) {
      toast.error('Erreur', { description: 'Utilisateur non connecté' });
      return;
    }

    setLinkLoadingPlatform(platform);

    try {
      const userResult = await getUserById(userIdToUse);
      if (!userResult.success || !userResult.data?.metricool_blog_id) {
        toast.error('Erreur', { description: 'Compte non configuré avec Metricool' });
        return;
      }

      const blogId = userResult.data.metricool_blog_id;
      const email = userResult.data.email ?? undefined;

      const result = await getConnectionLink(userIdToUse, blogId, { email });
      if (result.success && result.link) {
        openMetricoolUrl(result.link);
        toast.info('Connexion en cours', {
          description: `Connectez ${getPlatformName(platform)} dans le navigateur. Revenez ici après.`
        });
      } else {
        // Connection link failed - show fallback message and mark as failed
        setConnectionLinkFailed(true);
        const isTokenExpired = result.reason === 'token_expired' || result.error === 'TOKEN_EXPIRED';
        toast.info(isTokenExpired ? 'Session Metricool expirée' : 'Lien envoyé', {
          description: isTokenExpired
            ? 'Votre session Metricool a expiré. Vous recevrez un lien de reconnexion dans votre messagerie après votre onboarding.'
            : 'Vous allez recevoir le lien directement dans votre messagerie après votre onboarding.',
          duration: 6000,
        });
      }
    } catch (err) {
      // Important: never let an edge-function 500 crash the whole screen
      console.error('[handleConnectSocialNetwork] Error:', err);
      setConnectionLinkFailed(true);
      toast.info('Lien envoyé', {
        description: 'Vous allez recevoir le lien directement dans votre messagerie après votre onboarding.',
        duration: 6000,
      });
    } finally {
      setLinkLoadingPlatform(null);
    }
  };

  const handleTakePhoto = async () => {
    console.log('📸 Demande de prise de photo');
    console.log('📱 Environnement:', Capacitor.isNativePlatform() ? 'Native' : 'Web');
    console.log('🔧 Plateforme:', Capacitor.getPlatform());
    
    // Fermer le dialog AVANT l'opération et attendre que l'UI soit stable
    setIsPhotoDialogOpen(false);
    
    // Attendre que le dialog soit complètement fermé avant d'ouvrir le picker natif
    await new Promise(resolve => setTimeout(resolve, 300));
    
    try {
      const result = await takePhoto({
        quality: 90,
        allowEditing: false,
        width: 2048,
        height: 2048
      });
      
      if (result.success === true) {
        // Ouvrir le cropper avec la photo
        setTempImageForCrop(result.photo);
        setIsCropperOpen(true);
      } else if (result.success === false) {
        // Afficher l'erreur seulement si ce n'est pas une annulation utilisateur
        if (!result.cancelled) {
          console.error('❌ Erreur lors de la prise de photo:', result.error);
          toast.error('Erreur', {
            description: result.error || 'Impossible d\'accéder à la caméra'
          });
        }
        // Si cancelled=true, on ne fait rien (l'utilisateur a simplement annulé)
      }
    } catch (error) {
      console.error('❌ Erreur inattendue lors de la prise de photo:', error);
      toast.error('Erreur', {
        description: 'Une erreur inattendue est survenue'
      });
    }
  };

  const handleChooseFromGallery = async () => {
    console.log('🖼️ Ouverture de la galerie photo');
    console.log('📱 Environnement:', Capacitor.isNativePlatform() ? 'Native' : 'Web');
    console.log('🔧 Plateforme:', Capacitor.getPlatform());
    
    // Fermer le dialog AVANT l'opération et attendre que l'UI soit stable
    setIsPhotoDialogOpen(false);
    
    // Attendre que le dialog soit complètement fermé avant d'ouvrir le picker natif
    await new Promise(resolve => setTimeout(resolve, 300));
    
    try {
      const result = await pickPhotoFromLibrary({
        quality: 90,
        allowEditing: false,
        width: 2048,
        height: 2048
      });
      
      if (result.success === true) {
        // Ouvrir le cropper avec la photo
        setTempImageForCrop(result.photo);
        setIsCropperOpen(true);
      } else if (result.success === false) {
        // Afficher l'erreur seulement si ce n'est pas une annulation utilisateur
        if (!result.cancelled) {
          console.error('❌ Erreur lors du choix de photo:', result.error);
          toast.error('Erreur', {
            description: result.error || 'Impossible de sélectionner la photo'
          });
        }
        // Si cancelled=true, on ne fait rien (l'utilisateur a simplement annulé)
      }
    } catch (error) {
      console.error('❌ Erreur inattendue lors du choix de photo:', error);
      toast.error('Erreur', {
        description: 'Une erreur inattendue est survenue'
      });
    }
  };

  const handleCropComplete = async (croppedImageBlob: Blob) => {
    setIsCropperOpen(false);
    setTempImageForCrop('');

    // Créer une URL temporaire pour l'affichage immédiat
    const tempUrl = URL.createObjectURL(croppedImageBlob);
    setProfileImage(tempUrl);
    try {
      // Uploader vers Storage
      const publicUrl = await uploadAvatarToStorage(croppedImageBlob);
      if (publicUrl) {
        // Mettre à jour le profil avec l'URL publique
        const authState = useAuthStore.getState();
        if (authState.userId) {
          await updateAvatarUrl(authState.userId, publicUrl);
          // Refresh user profile in store to update sidebar
          await useUserStore.getState().refreshUserProfile(authState.userId);
        }

        // Nettoyer l'URL temporaire et utiliser l'URL publique
        URL.revokeObjectURL(tempUrl);
        setProfileImage(publicUrl);
        console.log('✅ Photo uploadée:', publicUrl);
      } else {
        // Remettre l'ancienne image en cas d'erreur
        URL.revokeObjectURL(tempUrl);
      }
    } catch (error) {
      console.error('❌ Erreur lors de l\'upload:', error);
      URL.revokeObjectURL(tempUrl);
    }
  };
  const handleCropCancel = () => {
    setIsCropperOpen(false);
    setTempImageForCrop('');
  };

  const handleDeletePhoto = async () => {
    setIsPhotoDialogOpen(false);
    
    const authState = useAuthStore.getState();
    if (!authState.userId) return;

    try {
      const deleted = await deleteAvatar(authState.userId);
      if (deleted) {
        await updateAvatarUrl(authState.userId, '');
        await useUserStore.getState().refreshUserProfile(authState.userId);
        setProfileImage('');
        toast.success('Photo de profil supprimée');
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('❌ Erreur lors de la suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Handler to navigate to the user's chat conversation
  const handleContactUser = async () => {
    const targetUserId = effectiveUserId || userId;
    if (!targetUserId) {
      navigate('/app/customer-chat');
      return;
    }

    // If viewing own profile (not impersonating someone else), go to customer chat
    const isOwnProfile = !effectiveUserId || effectiveUserId === userId;
    if (isOwnProfile && userId) {
      // Navigate to customer's own conversation view
      navigate('/app/customer-chat');
      return;
    }

    // Staff viewing another user's profile - open in staff chat
    const { data: conversation } = await getOrCreateConversationForUser(targetUserId);
    if (conversation) {
      // Use navigation store to open this conversation in StaffChatScreen
      useChatNavigationStore.getState().navigateToConversation(conversation.id);
      navigate('/app/staff-chat');
    } else {
      // Fallback to regular chat
      navigate('/app/customer-chat');
    }
  };

  const formatShortDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  };
  // Desktop Layout
  if (!isMobile) {
    return (
      <div className="min-h-screen bg-black relative">
        {/* Desktop Knowledge Base Wizard */}
        {showKnowledgeWizard && userId && (
          <div className="absolute inset-0 z-50 bg-black flex flex-col overflow-hidden">
            <KnowledgeBaseWizard
              userId={userId}
              variant="inline"
              onComplete={async () => {
                setShowKnowledgeWizard(false);
                await refreshKnowledgeBaseProgress();
                useKnowledgeBaseStore.getState().refetch();
              }}
              onBack={async () => {
                setShowKnowledgeWizard(false);
                await refreshKnowledgeBaseProgress();
                useKnowledgeBaseStore.getState().refetch();
              }}
            />
          </div>
        )}

            <DesktopProfileLayout
          firstName={firstName}
          lastName={lastName}
          email={email}
          profileImage={profileImage}
          assignedPBM={assignedPBM}
          assignedDirector={assignedDirector}
          assignedOps={assignedOps}
          hasKnowledgeBase={hasKnowledgeBase}
          knowledgeBaseProgress={knowledgeBaseProgress}
          socialNetworks={socialNetworks}
          disabledNetworks={disabledNetworks}
          onOpenSocialNetworks={() => navigate('/app/profile/social-networks')}
          globalFrequency={globalFrequency}
          isAutoPlanning={isAutoPlanning}
          hasAccess={hasAccess}
          shootingPrefs={shootingPrefs}
          thumbnailStyle={thumbnailStyle}
          hasDesignPrefs={hasDesignPrefs}
          toneOfVoice={toneOfVoice}
          hasEmojis={hasEmojis}
          hasRecurringCta={hasRecurringCta}
          hasDescriptionPrefs={hasDescriptionPrefs}
          onLogout={onLogout}
          onDeleteAccount={() => setShowDeleteDialog(true)}
          onEditProfile={() => navigate('/app/profile/edit')}
          onContact={handleContactUser}
          loadingState={loadingState}
        />

        {/* Delete Account Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent className="glass-strong border-white/20">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Êtes-vous absolument sûr ?</AlertDialogTitle>
              <AlertDialogDescription className="text-white/70">
                Cette action est irréversible. Cela supprimera définitivement votre compte
                et toutes vos données de nos serveurs.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="glass border-white/20 text-white hover:bg-white/10">
                Annuler
              </AlertDialogCancel>
              <AlertDialogAction onClick={() => {
                setShowDeleteDialog(false);
                onDeleteAccount();
              }} className="glass-strong border-red-500/30 bg-red-500/20 text-red-300 hover:bg-red-500/30">
                Oui, supprimer mon compte
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Photo Dialog */}
        <Dialog open={isPhotoDialogOpen} onOpenChange={setIsPhotoDialogOpen}>
          <DialogContent className="glass-strong border-white/20 text-white max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-white">Changer la photo de profil</DialogTitle>
              <DialogDescription className="text-white/70">
                Choisissez comment vous souhaitez ajouter votre photo
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 pt-4">
              <button onClick={handleTakePhoto} className="w-full glass rounded-2xl px-4 py-4 text-left hover:bg-white/10 active:scale-[0.98] transition-all">
                <div className="flex items-center gap-3">
                  <div className="glass-strong rounded-full p-2">
                    <Camera className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-white">Prendre une photo</span>
                </div>
              </button>
              
              <button onClick={handleChooseFromGallery} className="w-full glass rounded-2xl px-4 py-4 text-left hover:bg-white/10 active:scale-[0.98] transition-all">
                <div className="flex items-center gap-3">
                  <div className="glass-strong rounded-full p-2">
                    <ImageIcon className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-white">Choisir depuis la galerie</span>
                </div>
              </button>

              {profileImage && (
                <button onClick={handleDeletePhoto} className="w-full glass rounded-2xl px-4 py-4 text-left hover:bg-red-500/10 active:scale-[0.98] transition-all border border-red-500/20">
                  <div className="flex items-center gap-3">
                    <div className="glass-strong rounded-full p-2 border border-red-500/30">
                      <Trash2 className="h-5 w-5 text-red-400" />
                    </div>
                    <span className="text-red-300">Supprimer la photo</span>
                  </div>
                </button>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Image Cropper */}
        <ImageCropper image={tempImageForCrop} open={isCropperOpen} onComplete={handleCropComplete} onCancel={handleCropCancel} />
      </div>
    );
  }

  // Mobile Layout
  return <div className="min-h-screen pb-28 bg-black relative">
      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 space-y-6 pt-6">
        {/* Header - aligned with message button */}
          
          {/* Profil Section */}
          <Card className="glass-strong border-white/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <User className="h-5 w-5 text-white/70" />
                Informations personnelles
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Profile Image */}
              <div className="flex justify-center mb-6">
                <div className="relative group">
                  <button onClick={() => setIsPhotoDialogOpen(true)} className="relative focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-full active:scale-95 transition-all">
                    <Avatar className="h-24 w-24 ring-2 ring-white/20">
                      {profileImage && <AvatarImage src={profileImage} alt="Photo de profil" className="object-cover" />}
                      <AvatarFallback 
                        className="text-white text-2xl font-medium"
                        style={{ background: getGradientFromName(userData.firstName, userData.lastName) }}
                      >
                        {getInitials(userData.firstName, userData.lastName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="absolute bottom-0 right-0 glass-strong border-white/30 rounded-full p-2 hover:bg-white/20 transition-all">
                      <Camera className="h-4 w-4 text-white" />
                    </div>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-white/70 text-sm">Prénom</Label>
                  <Input id="firstName" type="text" value={firstName} onChange={e => {
                setFirstName(e.target.value);
                setHasPersonalInfoChanges(true);
              }} className="glass border-white/20 text-white placeholder:text-white/30 focus:border-white/40" placeholder="Votre prénom" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-white/70 text-sm">Nom</Label>
                  <Input id="lastName" type="text" value={lastName} onChange={e => {
                setLastName(e.target.value);
                setHasPersonalInfoChanges(true);
              }} className="glass border-white/20 text-white placeholder:text-white/30 focus:border-white/40" placeholder="Votre nom" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/70 text-sm">Email</Label>
                <Input id="email" type="email" value={email} readOnly className="glass border-white/20 text-white placeholder:text-white/30 focus:border-white/40" placeholder="votre.email@exemple.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-white/70 text-sm">Téléphone</Label>
                <Input id="phone" type="tel" value={phone} onChange={e => {
              setPhone(e.target.value);
              setHasPersonalInfoChanges(true);
            }} className="glass border-white/20 text-white placeholder:text-white/30 focus:border-white/40" placeholder="+33 6 12 34 56 78" />
              </div>

              {/* Job Title - visible only for admins/staff */}
              {isAdmin && (
                <div className="space-y-2">
                  <Label htmlFor="jobTitle" className="text-white/70 text-sm">Titre de poste</Label>
                  <Input id="jobTitle" type="text" value={jobTitle} onChange={e => {
                    setJobTitle(e.target.value);
                    setHasPersonalInfoChanges(true);
                  }} className="glass border-white/20 text-white placeholder:text-white/30 focus:border-white/40" placeholder="Ex: Personal Brand Manager" />
                </div>
              )}

              {/* Disponibilité — visible uniquement pour le staff */}
              {isAdmin && (
                <div className="space-y-3 pt-2">
                  <Separator className="bg-white/10" />
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-white text-sm font-medium">Mode Congé / Vacances</p>
                      <p className="text-white/50 text-xs mt-0.5">
                        {isOnLeave
                          ? 'Vos clients sont temporairement réassignés à vos collègues'
                          : 'Vos clients seront temporairement réassignés à vos collègues Ops'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {isLeaveLoading && (
                        <Loader2 className="h-4 w-4 animate-spin text-white/50" />
                      )}
                      <Switch
                        checked={isOnLeave}
                        disabled={isLeaveLoading}
                        onCheckedChange={async () => {
                          const authState = useAuthStore.getState();
                          if (!authState.userId) return;
                          const result = await toggleLeave(authState.userId);
                          if (!result.success) {
                            toast.error(`Impossible de modifier le mode congé : ${result.error}`);
                          } else if (result.reassigned_count !== undefined) {
                            toast.success(`✓ Congé activé — ${result.reassigned_count} client${result.reassigned_count === 1 ? '' : 's'} réassigné${result.reassigned_count === 1 ? '' : 's'}`);
                          } else if (result.restored_count !== undefined) {
                            toast.success(`✓ Retour confirmé — ${result.restored_count} client${result.restored_count === 1 ? '' : 's'} restauré${result.restored_count === 1 ? '' : 's'}`);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Bouton d'enregistrement des modifications */}
              {hasPersonalInfoChanges && <div className="pt-2">
                  <Button onClick={handleSavePersonalInfo} disabled={isSavingProfile} className="w-full bg-blue-500/20 border border-blue-400/30 text-white hover:bg-blue-500/30 active:scale-[0.98] transition-all backdrop-blur-sm disabled:opacity-50">
                    {isSavingProfile ? 'Enregistrement...' : <>
                        <Check className="h-4 w-4 mr-2" />
                        Enregistrer les modifications
                      </>}
                  </Button>
                </div>}

              {/* Change Password Button/Form */}
              <div className="pt-2">
                <Separator className="bg-white/10 mb-4" />
                {!isChangingPassword ? <Button onClick={() => setIsChangingPassword(true)} className="w-full bg-white/5 border border-white/20 text-white hover:bg-white/10 active:scale-[0.98] transition-all backdrop-blur-sm">
                    <Lock className="h-4 w-4 mr-2" />
                    Modifier mon mot de passe
                  </Button> : <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="current-password" className="text-white/70 text-sm">
                        Mot de passe actuel
                      </Label>
                      <Input id="current-password" type="password" value={currentPassword} onChange={e => {
                  setCurrentPassword(e.target.value);
                  setCurrentPasswordError('');
                }} className={`glass border-white/20 text-white placeholder:text-white/30 focus:border-white/40 ${currentPasswordError ? 'border-red-500/50' : ''}`} placeholder="••••••••" />
                      {currentPasswordError && <p className="text-red-400 text-xs mt-1">{currentPasswordError}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-password" className="text-white/70 text-sm">
                        Nouveau mot de passe
                      </Label>
                      <Input id="new-password" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="glass border-white/20 text-white placeholder:text-white/30 focus:border-white/40" placeholder="••••••••" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm-password" className="text-white/70 text-sm">
                        Confirmer le mot de passe
                      </Label>
                      <Input id="confirm-password" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="glass border-white/20 text-white placeholder:text-white/30 focus:border-white/40" placeholder="••••••••" />
                    </div>
                    <div className="flex gap-3 pt-2">
                      <Button onClick={() => {
                  setIsChangingPassword(false);
                  setCurrentPassword('');
                  setCurrentPasswordError('');
                  setNewPassword('');
                  setConfirmPassword('');
                }} variant="outline" className="flex-1 glass border-white/20 text-white hover:bg-white/10">
                        Annuler
                      </Button>
                      <Button onClick={handleChangePassword} disabled={isChangingPasswordLoading} className="flex-1 bg-blue-500/20 border border-blue-400/30 text-white hover:bg-blue-500/30 active:scale-[0.98] transition-all backdrop-blur-sm disabled:opacity-50">
                        {isChangingPasswordLoading ? 'Changement...' : 'Valider'}
                      </Button>
                    </div>
                  </div>}
              </div>
            </CardContent>
          </Card>

          {/* Staff attribué Section */}
          {(assignedDirector || assignedPBM || assignedOps) && <Card className="glass-strong border-white/20">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-white/10">
                    <Users className="h-5 w-5 text-foreground" />
                  </div>
                  <CardTitle className="text-white">Votre équipe</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {assignedPBM && <div className="glass rounded-2xl px-4 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-12 w-12 ring-2 ring-white/10">
                          {assignedPBM.avatar_url && <AvatarImage src={assignedPBM.avatar_url} alt={`${assignedPBM.first_name} ${assignedPBM.last_name}`} />}
                          <AvatarFallback className="bg-gradient-to-br from-purple-600 to-pink-500 text-white">
                            {assignedPBM.first_name?.[0] || ''}{assignedPBM.last_name?.[0] || ''}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="text-white text-sm font-medium">
                            {assignedPBM.first_name} {assignedPBM.last_name}
                          </p>
                          <p className="text-white/50 text-xs">Personal Brand Manager</p>
                        </div>
                      </div>
                    </div>}

                  {assignedDirector && <div className="glass rounded-2xl px-4 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-12 w-12 ring-2 ring-white/10">
                          {assignedDirector.avatar_url && <AvatarImage src={assignedDirector.avatar_url} alt={`${assignedDirector.first_name} ${assignedDirector.last_name}`} />}
                          <AvatarFallback className="bg-gradient-to-br from-amber-600 to-amber-500 text-black">
                            {assignedDirector.first_name?.[0] || ''}{assignedDirector.last_name?.[0] || ''}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="text-white text-sm font-medium">
                            {assignedDirector.first_name} {assignedDirector.last_name}
                          </p>
                          <p className="text-white/50 text-xs">Réalisateur</p>
                        </div>
                      </div>
                    </div>}

                  {assignedOps && <div className="glass rounded-2xl px-4 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-12 w-12 ring-2 ring-white/10">
                          {assignedOps.avatar_url && <AvatarImage src={assignedOps.avatar_url} alt={`${assignedOps.first_name} ${assignedOps.last_name}`} />}
                          <AvatarFallback className="bg-gradient-to-br from-emerald-600 to-teal-500 text-white">
                            {assignedOps.first_name?.[0] || ''}{assignedOps.last_name?.[0] || ''}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="text-white text-sm font-medium">
                            {assignedOps.first_name} {assignedOps.last_name}
                          </p>
                          <p className="text-white/50 text-xs">{assignedOps.job_title || 'Ops'}</p>
                        </div>
                      </div>
                    </div>}
                </div>
              </CardContent>
            </Card>}

          {/* Social Networks Card - Navigate to dedicated page like desktop */}
          <ProfileCard 
            delay={0.1} 
            onClick={() => navigate('/app/profile/social-networks')}
            className="group p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ProfileCardIcon 
                  icon={<Link2 />} 
                  isComplete={socialNetworks.filter(n => n.connected).length === socialNetworks.filter(n => !disabledNetworks.includes(n.platform)).length && socialNetworks.filter(n => !disabledNetworks.includes(n.platform)).length > 0}
                  isPending={socialNetworks.filter(n => n.connected).length !== socialNetworks.filter(n => !disabledNetworks.includes(n.platform)).length}
                  showCompletionBadge={true}
                />
                <div>
                  <h3 className="font-medium text-[hsl(var(--v10-text-primary))]">Connexion réseaux sociaux</h3>
                  <div className="mt-1">
                    <ProfileCardStatus 
                      isComplete={socialNetworks.filter(n => n.connected).length === socialNetworks.filter(n => !disabledNetworks.includes(n.platform)).length && socialNetworks.filter(n => !disabledNetworks.includes(n.platform)).length > 0}
                      completeText={`${socialNetworks.filter(n => n.connected).length}/${socialNetworks.filter(n => !disabledNetworks.includes(n.platform)).length} connectés`}
                      incompleteText={`${socialNetworks.filter(n => n.connected).length}/${socialNetworks.filter(n => !disabledNetworks.includes(n.platform)).length} connectés`}
                    />
                  </div>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-[hsl(var(--v10-text-tertiary))]" />
            </div>
          </ProfileCard>

          {/* Knowledge Base Card */}
          <ProfileCard 
            delay={0.2}
            onClick={() => {
              if (userId) {
                navigate('/app/profile/knowledge-base');
              }
            }}
            className="group p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ProfileCardIcon 
                  icon={<FileText />} 
                  isComplete={hasKnowledgeBase}
                  isPending={!hasKnowledgeBase}
                  showCompletionBadge={true}
                />
                <div>
                  <h3 className="font-medium text-[hsl(var(--v10-text-primary))]">Base de Connaissance</h3>
                  <div className="mt-1">
                    <ProfileCardStatus 
                      isComplete={hasKnowledgeBase}
                      completeText="Profil stratégique complété"
                      incompleteText={`${knowledgeBaseProgress.completed}/${knowledgeBaseProgress.total} questions complétées`}
                    />
                  </div>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-[hsl(var(--v10-text-tertiary))]" />
            </div>
          </ProfileCard>

          {/* Shooting Preferences Card */}
          <ProfileCard 
            delay={0.3}
            onClick={() => navigate('/app/profile/shooting-preferences')}
            className="group p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ProfileCardIcon 
                  icon={<Armchair />} 
                  isComplete={!!shootingPrefs?.installationType && !!shootingPrefs?.decorColor}
                  isPending={!shootingPrefs?.installationType || !shootingPrefs?.decorColor}
                  showCompletionBadge={true}
                />
                <div>
                  <h3 className="font-medium text-[hsl(var(--v10-text-primary))]">Préférences de tournage</h3>
                  <div className="mt-1">
                    <ProfileCardStatus 
                      isComplete={!!shootingPrefs?.installationType && !!shootingPrefs?.decorColor}
                      completeText={
                        <span className="flex items-center gap-2">
                          {shootingPrefs?.decorColor && (
                            <span 
                              className="w-3 h-3 rounded-full border border-white/20 inline-block" 
                              style={{ backgroundColor: shootingPrefs.decorColor }}
                            />
                          )}
                          {shootingPrefs?.installationType === 'fauteuil' ? 'Fauteuil' : shootingPrefs?.installationType === 'tabouret' ? 'Tabouret' : 'Bureau'}
                        </span>
                      }
                    />
                  </div>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-[hsl(var(--v10-text-tertiary))]" />
            </div>
          </ProfileCard>

          {/* Design Preferences Card */}
          <ProfileCard 
            delay={0.4}
            onClick={() => navigate('/app/profile/design-preferences')}
            className="group p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ProfileCardIcon 
                  icon={<Image />} 
                  isComplete={hasDesignPrefs}
                  isPending={!hasDesignPrefs}
                  showCompletionBadge={true}
                />
                <div>
                  <h3 className="font-medium text-[hsl(var(--v10-text-primary))]">Préférences de design</h3>
                  <div className="mt-1">
                    <ProfileCardStatus 
                      isComplete={hasDesignPrefs}
                      completeText={thumbnailStyle === 'bold' ? 'Bold' : thumbnailStyle === 'classique' ? 'Classique' : thumbnailStyle === 'custom' ? 'Custom' : thumbnailStyle === 'none' ? 'Aucun' : 'Défini'}
                    />
                  </div>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-[hsl(var(--v10-text-tertiary))]" />
            </div>
          </ProfileCard>

          {/* Description Preferences Card */}
          <ProfileCard 
            delay={0.5}
            onClick={() => navigate('/app/profile/description-preferences')}
            className="group p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ProfileCardIcon 
                  icon={<MessageSquare />} 
                  isComplete={hasDescriptionPrefs}
                  isPending={!hasDescriptionPrefs}
                  showCompletionBadge={true}
                />
                <div>
                  <h3 className="font-medium text-[hsl(var(--v10-text-primary))]">Préférences de description</h3>
                  <div className="mt-1">
                    {hasDescriptionPrefs ? (
                      <div className="flex items-center gap-1 text-xs text-[hsl(var(--v10-accent))]">
                        <CheckCircle2 className="w-3 h-3 shrink-0" />
                        <span>{toneOfVoice === 'tu' ? 'Tutoiement' : 'Vouvoiement'}</span>
                        <span className="text-[hsl(var(--v10-text-tertiary))]">·</span>
                        <span>{hasEmojis ? 'Emojis' : 'Sans emojis'}</span>
                        <span className="text-[hsl(var(--v10-text-tertiary))]">·</span>
                        <span>{hasRecurringCta ? 'CTA' : 'Sans CTA'}</span>
                      </div>
                    ) : (
                      <ProfileCardStatus isComplete={false} />
                    )}
                  </div>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-[hsl(var(--v10-text-tertiary))]" />
            </div>
          </ProfileCard>

          {/* User Stats Section - Mobile only, users with user_stats access */}
          {isMobile && hasAccess('user_stats') && (
            <ProfileCard 
              delay={0.6}
              onClick={() => navigate('/app/user-stats')}
              className="group p-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ProfileCardIcon icon={<BarChart3 />} />
                  <div>
                    <h3 className="font-medium text-[hsl(var(--v10-text-primary))]">Mes statistiques</h3>
                    <p className="text-sm text-[hsl(var(--v10-text-secondary))]">
                      Consultez vos performances
                    </p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-[hsl(var(--v10-text-tertiary))]" />
              </div>
            </ProfileCard>
          )}

          {/* User Assets Section - Admin only */}
          {isAdmin && <UserAssetsSection />}

          {/* Planning Preferences Section - users with planning or rescheduling access */}
          {(hasAccess('admin_planning_setup') || hasAccess('admin_rescheduling_setup')) && (
            <ProfileCard 
              delay={0.7}
              onClick={() => navigate('/app/admin-planning-setup')}
              className="group p-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ProfileCardIcon 
                    icon={<Calendar />} 
                    isComplete={isAutoPlanning !== null}
                    isPending={isAutoPlanning === null}
                    showCompletionBadge={true}
                  />
                  <div>
                    <h3 className="font-medium text-[hsl(var(--v10-text-primary))]">Préférences de Planning</h3>
                    <div className="mt-1">
                      <ProfileCardStatus 
                        isComplete={isAutoPlanning !== null}
                        completeText={isAutoPlanning ? 'Optimisé par Prsnl' : 'Planification manuelle'}
                      />
                    </div>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-[hsl(var(--v10-text-tertiary))]" />
              </div>
            </ProfileCard>
          )}

          {/* Photo Dialog */}
          <Dialog open={isPhotoDialogOpen} onOpenChange={setIsPhotoDialogOpen}>
            <DialogContent className="glass-strong border-white/20 text-white max-w-sm">
              <DialogHeader>
                <DialogTitle className="text-white">Changer la photo de profil</DialogTitle>
                <DialogDescription className="text-white/70">
                  Choisissez comment vous souhaitez ajouter votre photo
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 pt-4">
                <button onClick={handleTakePhoto} className="w-full glass rounded-2xl px-4 py-4 text-left hover:bg-white/10 active:scale-[0.98] transition-all">
                  <div className="flex items-center gap-3">
                    <div className="glass-strong rounded-full p-2">
                      <Camera className="h-5 w-5 text-white" />
                    </div>
                    <span className="text-white">Prendre une photo</span>
                  </div>
                </button>
                
                <button onClick={handleChooseFromGallery} className="w-full glass rounded-2xl px-4 py-4 text-left hover:bg-white/10 active:scale-[0.98] transition-all">
                  <div className="flex items-center gap-3">
                    <div className="glass-strong rounded-full p-2">
                      <ImageIcon className="h-5 w-5 text-white" />
                    </div>
                    <span className="text-white">Choisir depuis la galerie</span>
                  </div>
                </button>

                {profileImage && (
                  <button onClick={handleDeletePhoto} className="w-full glass rounded-2xl px-4 py-4 text-left hover:bg-red-500/10 active:scale-[0.98] transition-all border border-red-500/20">
                    <div className="flex items-center gap-3">
                      <div className="glass-strong rounded-full p-2 bg-red-500/10">
                        <Trash2 className="h-5 w-5 text-red-400" />
                      </div>
                      <span className="text-red-400">Supprimer la photo</span>
                    </div>
                  </button>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Invoices Section */}
          {/* <Card className="glass-strong border-white/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <FileText className="h-5 w-5 text-white/70" />
                Mes factures
              </CardTitle>
            </CardHeader>
            <CardContent>
              {userData.invoices.length === 0 ? (
                <p className="text-white/50 text-center py-8">
                  Aucune facture disponible
                </p>
              ) : (
                <div className="space-y-3">
                  {userData.invoices.map((invoice, index) => (
                    <div key={invoice.id}>
                      <button
                        onClick={() => handleDownloadInvoice(invoice.plan, invoice.phase, invoice.totalPhases)}
                        className="w-full glass glass-interactive rounded-2xl px-4 py-4 text-left active:scale-[0.98] transition-transform"
                      >
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <p className="text-white">{invoice.plan} {invoice.phase}/{invoice.totalPhases}</p>
                            <p className="text-white/50 text-sm">
                              {formatShortDate(invoice.date)}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="text-white">{invoice.amount}€ HT</p>
                              <p className={`text-xs ${invoice.status === 'À venir' ? 'text-amber-400' : 'text-green-400'}`}>
                                {invoice.status}
                              </p>
                            </div>
                            <ChevronRight className="h-4 w-4 text-white/50" />
                          </div>
                        </div>
                      </button>
                      {index < userData.invoices.length - 1 && (
                        <div className="h-3" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
           </Card> */}

          {/* Admin Section - Visible on mobile if user has access to at least one admin feature */}
          {isMobile && adminMenuItems.some(item => {
            const service = VIEW_TO_SERVICE[item.view];
            return service ? hasAccess(service) : false;
          }) && (
            <Card className="glass-strong border-white/20">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-white/10">
                    <Shield className="h-5 w-5 text-foreground" />
                  </div>
                  <CardTitle className="text-white">Administration</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {adminMenuItems
                  .filter(item => {
                    const service = VIEW_TO_SERVICE[item.view];
                    return service ? hasAccess(service) : false;
                  })
                  .map((item) => {
                    const Icon = item.icon;
                    return (
                      <Button 
                        key={item.view}
                        variant="ghost" 
                        onClick={() => navigate(`/app/${item.view}`)} 
                        className="w-full glass glass-interactive border-white/20 text-white hover:bg-white/10 active:scale-[0.98] transition-all backdrop-blur-sm justify-start"
                      >
                        <Icon className="h-4 w-4 mr-2" />
                        {item.label}
                      </Button>
                    );
                  })}
              </CardContent>
            </Card>
          )}

          {/* Aide & Documentation - Mobile only */}
          {isMobile && (
            <Card className="glass-strong border-white/20">
              <CardContent className="p-3">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/app/help')}
                  className="w-full glass glass-interactive border-white/20 text-white hover:bg-white/10 active:scale-[0.98] transition-all backdrop-blur-sm"
                >
                  <HelpCircle className="h-4 w-4 mr-2" />
                  Aide & Documentation
                </Button>
              </CardContent>
            </Card>
          )}

          <Card className="glass-strong border-white/20">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-white/10">
                  <User className="h-5 w-5 text-foreground" />
                </div>
                <CardTitle className="text-white">Mon compte</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button variant="ghost" onClick={onLogout} className="w-full glass glass-interactive border-white/20 text-white hover:bg-white/10 active:scale-[0.98] transition-all backdrop-blur-sm">
                <LogOut className="h-4 w-4 mr-2" />
                Se déconnecter
              </Button>
              
              <Button variant="ghost" onClick={() => setShowDeleteDialog(true)} className="w-full glass border-red-500/30 bg-red-500/20 text-red-300 hover:bg-red-500/30 active:scale-[0.98] transition-all backdrop-blur-sm">
                <Trash2 className="h-4 w-4 mr-2" />
                Supprimer mon compte
              </Button>
            </CardContent>
          </Card>

          {/* Bottom spacing */}
          <div className="h-8" />
        </div>

      {/* Knowledge Base Modal - Liste */}
      {showKnowledgeBase && userId && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto">
          <div className="h-full w-full max-w-2xl p-6">
            <div className="mb-4">
              <Button variant="ghost" onClick={() => setShowKnowledgeBase(false)} className="text-white hover:bg-white/10">
                <ChevronRight className="h-5 w-5 mr-2 rotate-180" />
                Retour
              </Button>
            </div>
            <KnowledgeBaseForm userId={userId} onSaved={() => setHasKnowledgeBase(true)} />
            <div className="h-8" />
          </div>
        </div>
      )}

      {/* Knowledge Base Wizard - Mobile only (desktop handled above) */}
      {isMobile && showKnowledgeWizard && userId && (
        <KnowledgeBaseWizard
          userId={userId}
          variant="card"
          onComplete={async () => {
            setShowKnowledgeWizard(false);
            await refreshKnowledgeBaseProgress();
            useKnowledgeBaseStore.getState().refetch();
          }}
          onBack={async () => {
            setShowKnowledgeWizard(false);
            await refreshKnowledgeBaseProgress();
            useKnowledgeBaseStore.getState().refetch();
          }}
        />
      )}

      {/* Delete Account Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="glass-strong border-white/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Êtes-vous absolument sûr ?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/70">
              Cette action est irréversible. Cela supprimera définitivement votre compte
              et toutes vos données de nos serveurs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="glass border-white/20 text-white hover:bg-white/10">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => {
            setShowDeleteDialog(false);
            onDeleteAccount();
          }} className="glass-strong border-red-500/30 bg-red-500/20 text-red-300 hover:bg-red-500/30">
              Oui, supprimer mon compte
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Image Cropper */}
      <ImageCropper image={tempImageForCrop} open={isCropperOpen} onComplete={handleCropComplete} onCancel={handleCropCancel} />

      {/* Notification Onboarding */}
      {showNotificationOnboarding && userId && createPortal(<NotificationOnboardingScreen userId={userId} onComplete={() => setShowNotificationOnboarding(false)} />, document.body)}

      {appVersion && (
        <div className="py-4 text-center">
          <span className="text-xs text-white/50">Version {appVersion}</span>
        </div>
      )}
    </div>;
}